import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { useCreateTemplateMutation, useUpdateTemplateMutation, CreateTemplatePayload, UpdateTemplatePayload, TemplateComponent } from '@/client/api/whatsapp/create-template.mutation';
import { Template } from '@/client/types/whatsapp/templates';
import { toast } from 'sonner';

interface TemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  template?: Template | null;
}

const TemplateModal: React.FC<TemplateModalProps> = ({ 
  open, 
  onOpenChange, 
  onSuccess,
  template
}) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<'marketing' | 'utility' | 'authentication'>('marketing');
  const [language, setLanguage] = useState('en_US');
  const [components, setComponents] = useState<(TemplateComponent & { variables?: { param_name: string; example: string }[] })[]>([
    { type: 'body', text: '', variables: [] }
  ]);

  const isEditing = !!template;

  useEffect(() => {
    if (template && open) {
      setName(template.name);
      setCategory(template.category.toLowerCase() as any);
      setLanguage(template.language);
      
      const mappedComponents = template.components.map(c => {
        const type = c.type.toLowerCase() as TemplateComponent['type'];
        const mapped: TemplateComponent & { variables?: { param_name: string; example: string }[] } = {
          type,
          text: (c as any).text || ''
        };
        
        if (type === 'header') {
          mapped.format = (c as any).format || 'TEXT';
        }
        
        if (type === 'body') {
          // Extract variables from example if available
          const example = (c as any).example;
          if (example?.body_text_named_params) {
            mapped.variables = example.body_text_named_params.map((p: any) => ({
              param_name: p.param_name,
              example: p.example
            }));
          } else {
            mapped.variables = [];
          }
        }
        
        return mapped;
      });
      
      setComponents(mappedComponents);
    } else if (!template && open) {
      // Reset for create
      setName('');
      setCategory('marketing');
      setLanguage('en_US');
      setComponents([{ type: 'body', text: '', variables: [] }]);
    }
  }, [template, open]);

  const { mutate: createTemplate, isPending: isCreating } = useCreateTemplateMutation();
  const { mutate: updateTemplate, isPending: isUpdating } = useUpdateTemplateMutation();

  const isPending = isCreating || isUpdating;

  const handleAddComponent = (type: TemplateComponent['type']) => {
    if (type === 'header' && components.some(c => c.type === 'header')) return;
    if (type === 'footer' && components.some(c => c.type === 'footer')) return;
    
    const newComponent: TemplateComponent & { variables?: { param_name: string; example: string }[] } = { type, text: '' };
    if (type === 'header') newComponent.format = 'TEXT';
    if (type === 'body') newComponent.variables = [];
    
    setComponents([...components, newComponent]);
  };

  const handleRemoveComponent = (index: number) => {
    setComponents(components.filter((_, i) => i !== index));
  };

  const handleComponentChange = (index: number, field: keyof TemplateComponent, value: any) => {
    const newComponents = [...components];
    newComponents[index] = { ...newComponents[index], [field]: value };
    setComponents(newComponents);
  };

  const handleAddVariable = (index: number) => {
    const newComponents = [...components];
    const component = newComponents[index];
    if (component.type === 'body') {
      component.variables = [...(component.variables || []), { param_name: '', example: '' }];
      setComponents(newComponents);
    }
  };

  const handleRemoveVariable = (componentIndex: number, variableIndex: number) => {
    const newComponents = [...components];
    const component = newComponents[componentIndex];
    if (component.type === 'body' && component.variables) {
      component.variables = component.variables.filter((_, i) => i !== variableIndex);
      setComponents(newComponents);
    }
  };

  const handleVariableChange = (componentIndex: number, variableIndex: number, field: 'param_name' | 'example', value: string) => {
    const newComponents = [...components];
    const component = newComponents[componentIndex];
    if (component.type === 'body' && component.variables) {
      component.variables[variableIndex][field] = value;
      setComponents(newComponents);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name) {
      toast.error('Template name is required');
      return;
    }

    if (components.some(c => c.type === 'body' && !c.text)) {
      toast.error('Body text is required');
      return;
    }

    const payloadBase = {
      name: name.toLowerCase().replace(/\s+/g, '_'),
      category,
      language,
      components: components.map(({ variables, ...c }) => {
        const component: TemplateComponent = { ...c };
        if (c.type === 'body' && variables && variables.length > 0) {
          component.example = {
            body_text_named_params: variables.filter(v => v.param_name && v.example)
          };
        }
        return component;
      }),
      allow_category_change: true
    };

    if (isEditing && template) {
      const payload: UpdateTemplatePayload = {
        ...payloadBase,
        id: template.id
      };
      
      updateTemplate(payload, {
        onSuccess: () => {
          toast.success('Template updated successfully');
          onSuccess();
          onOpenChange(false);
        },
        onError: (err: any) => {
          toast.error(err.message || 'Failed to update template');
        }
      });
    } else {
      createTemplate(payloadBase, {
        onSuccess: () => {
          toast.success('Template created successfully and sent for review');
          onSuccess();
          onOpenChange(false);
          // Reset form
          setName('');
          setComponents([{ type: 'body', text: '', variables: [] }]);
        },
        onError: (err: any) => {
          toast.error(err.message || 'Failed to create template');
        }
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit WhatsApp Template' : 'Create WhatsApp Template'}</DialogTitle>
          <DialogDescription>
            {isEditing 
              ? 'Editing an approved template will resubmit it for review.'
              : 'Templates must be approved by Meta before they can be used.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Template Name</Label>
              <Input 
                id="name" 
                placeholder="e.g. order_confirmation" 
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={isEditing}
              />
              <p className="text-[10px] text-muted-foreground">
                {isEditing ? 'Template name cannot be changed.' : 'Lowercase, underscores, no spaces.'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value as any)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="marketing">Marketing</option>
                <option value="utility">Utility</option>
                <option value="authentication">Authentication</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="language">Language</Label>
            <select
              id="language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="en_US">English (US)</option>
              <option value="en_GB">English (UK)</option>
              <option value="fr">French</option>
              <option value="es">Spanish</option>
              {/* Add more as needed */}
            </select>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Components</Label>
              <div className="flex gap-2">
                {!components.some(c => c.type === 'header') && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleAddComponent('header')}
                  >
                    <Plus className="w-3 h-3 mr-1" /> Header
                  </Button>
                )}
                {!components.some(c => c.type === 'footer') && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleAddComponent('footer')}
                  >
                    <Plus className="w-3 h-3 mr-1" /> Footer
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-4">
              {components.map((component, index) => (
                <div key={index} className="p-4 border rounded-lg relative space-y-3 bg-slate-50/50">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase text-muted-foreground">
                      {component.type}
                    </span>
                    {component.type !== 'body' && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-destructive"
                        onClick={() => handleRemoveComponent(index)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>

                  {component.type === 'header' && (
                    <div className="space-y-3">
                       <select
                        value={component.format}
                        onChange={(e) => handleComponentChange(index, 'format', e.target.value)}
                        className="flex h-8 w-full rounded-md border border-input bg-transparent px-3 text-xs shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      >
                        <option value="TEXT">Text</option>
                        <option value="IMAGE">Image</option>
                        <option value="VIDEO">Video</option>
                        <option value="DOCUMENT">Document</option>
                      </select>
                      {component.format === 'TEXT' && (
                        <Input
                          placeholder="Header text..."
                          value={component.text}
                          onChange={(e) => handleComponentChange(index, 'text', e.target.value)}
                          maxLength={60}
                        />
                      )}
                    </div>
                  )}

                  {component.type === 'body' && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <textarea
                          placeholder="Body message content..."
                          value={component.text}
                          onChange={(e) => handleComponentChange(index, 'text', e.target.value)}
                          className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                          required
                        />
                        <p className="text-[10px] text-muted-foreground">
                          Use {'{{variable_name}}'} for named variables.
                        </p>
                      </div>

                      <div className="space-y-3 border-t pt-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs">Variables & Examples</Label>
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 text-[10px]"
                            onClick={() => handleAddVariable(index)}
                          >
                            <Plus className="w-3 h-3 mr-1" /> Add Variable
                          </Button>
                        </div>
                        
                        <div className="space-y-2">
                          {component.variables?.map((v, vIndex) => (
                            <div key={vIndex} className="grid grid-cols-[1fr,1fr,auto] gap-2 items-center">
                              <Input 
                                placeholder="Variable (e.g. first_name)" 
                                value={v.param_name}
                                onChange={(e) => handleVariableChange(index, vIndex, 'param_name', e.target.value)}
                                className="h-8 text-xs"
                              />
                              <Input 
                                placeholder="Example (e.g. Pablo)" 
                                value={v.example}
                                onChange={(e) => handleVariableChange(index, vIndex, 'example', e.target.value)}
                                className="h-8 text-xs"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-destructive"
                                onClick={() => handleRemoveVariable(index, vIndex)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                          {(!component.variables || component.variables.length === 0) && (
                            <p className="text-[10px] text-center text-muted-foreground py-2 italic">
                              No variables added yet.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {component.type === 'footer' && (
                    <Input
                      placeholder="Footer text..."
                      value={component.text}
                      onChange={(e) => handleComponentChange(index, 'text', e.target.value)}
                      maxLength={60}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEditing ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateModal;
